// Mercapoli - Admin Restaurant Form (Create/Edit)
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Store, Loader2, Save, Plus, Trash2, Upload, X, Image as ImageIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { admin as adminApi, config as configApi, restaurantes as restaurantesApi, categorias as categoriasApi } from '../../lib/api';
import { toast } from 'sonner';

const CLOUDINARY_UPLOAD_PRESET = 'mercapoli_unsigned';
const CLOUDINARY_CLOUD_NAME = 'mercapoli';

const RestaurantForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const matrizId = searchParams.get('matriz'); // Para crear sucursal
  
  const isEditing = !!id;
  const isSucursal = !!matrizId;
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [paises, setPaises] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [matrizInfo, setMatrizInfo] = useState(null);
  
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    direccion: '',
    ciudad: '',
    pais: 'GT',
    telefono: '',
    email: '',
    categoria_ids: [],
    imagen: '',
    logo: '',
    horario_apertura: '08:00',
    horario_cierre: '22:00',
    tiempo_preparacion_min: 20,
    costo_envio: 15.00,
    calificacion: 4.5,
    latitud: null,
    longitud: null,
    nit_rfc: '',
    razon_social: '',
    cuenta_banco: '',
    banco: '',
    es_sucursal: false,
    comercio_matriz_id: null,
    comision_porcentaje: 15.0,
    activo: true
  });

  useEffect(() => {
    loadInitialData();
  }, [id, matrizId]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Cargar países
      const paisesData = await configApi.getPaises();
      setPaises(paisesData.paises);
      
      // Cargar categorías
      const cats = await categoriasApi.getAll();
      setCategorias(cats);
      
      // Si es edición, cargar datos del comercio
      if (isEditing) {
        const comercio = await restaurantesApi.getById(id);
        setFormData({
          nombre: comercio.nombre || '',
          descripcion: comercio.descripcion || '',
          direccion: comercio.direccion || '',
          ciudad: comercio.ciudad || '',
          pais: comercio.pais || 'GT',
          telefono: comercio.telefono || '',
          email: comercio.email || '',
          categoria_ids: comercio.categoria_ids || [],
          imagen: comercio.imagen || '',
          logo: comercio.logo || '',
          horario_apertura: comercio.horario_apertura || '08:00',
          horario_cierre: comercio.horario_cierre || '22:00',
          tiempo_preparacion_min: comercio.tiempo_preparacion_min || 20,
          costo_envio: comercio.costo_envio || 15.00,
          calificacion: comercio.calificacion || 4.5,
          latitud: comercio.latitud || null,
          longitud: comercio.longitud || null,
          nit_rfc: comercio.nit_rfc || '',
          razon_social: comercio.razon_social || '',
          cuenta_banco: comercio.cuenta_banco || '',
          banco: comercio.banco || '',
          es_sucursal: comercio.es_sucursal || false,
          comercio_matriz_id: comercio.comercio_matriz_id || null,
          comision_porcentaje: comercio.comision_porcentaje || 15.0,
          activo: comercio.activo !== false
        });
      }
      
      // Si es nueva sucursal, cargar info de la matriz
      if (matrizId) {
        const matriz = await restaurantesApi.getById(matrizId);
        setMatrizInfo(matriz);
        setFormData(prev => ({
          ...prev,
          es_sucursal: true,
          comercio_matriz_id: matrizId,
          pais: matriz.pais,
          comision_porcentaje: matriz.comision_porcentaje,
          categoria_ids: matriz.categoria_ids
        }));
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar datos');
    }
    setLoading(false);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Subir imagen a Cloudinary
  const uploadImage = async (file, type) => {
    if (!file) return null;

    // Validaciones
    const maxSize = type === 'banner' ? 5 * 1024 * 1024 : 2 * 1024 * 1024;
    const maxSizeLabel = type === 'banner' ? '5MB' : '2MB';
    
    if (file.size > maxSize) {
      toast.error(`La imagen no debe superar ${maxSizeLabel}`);
      return null;
    }

    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Formato no válido. Usa JPG, PNG o WebP');
      return null;
    }

    const setUploading = type === 'banner' ? setUploadingBanner : setUploadingLogo;
    setUploading(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      formDataUpload.append('folder', `mercapoli/comercios/${type}`);

      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
        { method: 'POST', body: formDataUpload }
      );

      if (!response.ok) throw new Error('Upload failed');

      const data = await response.json();
      
      const field = type === 'banner' ? 'imagen' : 'logo';
      setFormData(prev => ({ ...prev, [field]: data.secure_url }));
      toast.success(`${type === 'banner' ? 'Banner' : 'Logo'} subido correctamente`);
      
      return data.secure_url;
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(`Error al subir ${type === 'banner' ? 'banner' : 'logo'}`);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleBannerUpload = (e) => uploadImage(e.target.files?.[0], 'banner');
  const handleLogoUpload = (e) => uploadImage(e.target.files?.[0], 'logo');

  const removeBanner = () => setFormData(prev => ({ ...prev, imagen: '' }));
  const removeLogo = () => setFormData(prev => ({ ...prev, logo: '' }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!formData.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    if (!formData.direccion.trim()) {
      toast.error('La dirección es requerida');
      return;
    }
    if (!formData.telefono.trim()) {
      toast.error('El teléfono es requerido');
      return;
    }
    
    setSaving(true);
    try {
      const dataToSend = {
        ...formData,
        tiempo_preparacion_min: parseInt(formData.tiempo_preparacion_min),
        costo_envio: parseFloat(formData.costo_envio),
        calificacion: parseFloat(formData.calificacion),
        comision_porcentaje: parseFloat(formData.comision_porcentaje),
        latitud: formData.latitud ? parseFloat(formData.latitud) : null,
        longitud: formData.longitud ? parseFloat(formData.longitud) : null
      };
      
      if (isEditing) {
        await adminApi.updateComercio(id, dataToSend);
        toast.success('Comercio actualizado');
      } else {
        await adminApi.createComercio(dataToSend);
        toast.success(isSucursal ? 'Sucursal creada' : 'Comercio creado');
      }
      
      navigate('/admin/restaurantes');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
    setSaving(false);
  };

  const paisActual = paises.find(p => p.codigo === formData.pais);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="container-app">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/admin/restaurantes')} className="p-2 -ml-2 rounded-lg hover:bg-muted">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="font-heading text-lg font-bold">
                {isEditing ? 'Editar Comercio' : isSucursal ? 'Nueva Sucursal' : 'Nuevo Comercio'}
              </h1>
            </div>
            <Button 
              onClick={handleSubmit}
              disabled={saving}
              className="bg-brand-primary hover:bg-brand-secondary"
              data-testid="save-restaurant-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Guardar
            </Button>
          </div>
        </div>
      </header>

      <div className="container-app py-6">
        {matrizInfo && (
          <div className="mb-6 p-4 bg-brand-primary/10 rounded-lg border border-brand-primary/20">
            <p className="text-sm text-muted-foreground">Creando sucursal de:</p>
            <p className="font-medium">{matrizInfo.nombre}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Información Básica */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Store className="h-5 w-5" />
                Información Básica
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="nombre">Nombre del Comercio *</Label>
                  <Input
                    id="nombre"
                    value={formData.nombre}
                    onChange={(e) => handleChange('nombre', e.target.value)}
                    placeholder="Ej: Tacos El Güero"
                    data-testid="restaurant-name"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="pais">País *</Label>
                  <Select value={formData.pais} onValueChange={(v) => handleChange('pais', v)}>
                    <SelectTrigger data-testid="restaurant-country">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {paises.map(p => (
                        <SelectItem key={p.codigo} value={p.codigo}>
                          {p.nombre} ({p.moneda})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="descripcion">Descripción</Label>
                <Textarea
                  id="descripcion"
                  value={formData.descripcion}
                  onChange={(e) => handleChange('descripcion', e.target.value)}
                  placeholder="Describe el comercio..."
                  rows={3}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="direccion">Dirección *</Label>
                  <Input
                    id="direccion"
                    value={formData.direccion}
                    onChange={(e) => handleChange('direccion', e.target.value)}
                    placeholder="Calle, número, colonia"
                    data-testid="restaurant-address"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="ciudad">Ciudad *</Label>
                  <Input
                    id="ciudad"
                    value={formData.ciudad}
                    onChange={(e) => handleChange('ciudad', e.target.value)}
                    placeholder="Ej: Ciudad de Guatemala"
                    data-testid="restaurant-city"
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="telefono">Teléfono *</Label>
                  <Input
                    id="telefono"
                    value={formData.telefono}
                    onChange={(e) => handleChange('telefono', e.target.value)}
                    placeholder="+502 1234 5678"
                    data-testid="restaurant-phone"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="contacto@comercio.com"
                  />
                </div>
              </div>

              {/* Banner Principal - Upload Cloudinary */}
              <div className="space-y-2">
                <Label>
                  Banner Principal
                  <span className="text-xs text-muted-foreground ml-2">
                    (Recomendado: 1600x600, JPG/PNG/WebP, máx 5MB)
                  </span>
                </Label>
                <div className="flex items-start gap-4">
                  {formData.imagen ? (
                    <div className="relative w-48 h-20 rounded-lg overflow-hidden border">
                      <img 
                        src={formData.imagen} 
                        alt="Banner" 
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={removeBanner}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                      >
                        <X className="h-4 w-4 text-white" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-48 h-20 rounded-lg border-2 border-dashed border-border flex items-center justify-center">
                      <ImageIcon className="h-8 w-8 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1">
                    <Input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleBannerUpload}
                      disabled={uploadingBanner}
                      className="max-w-[250px]"
                      data-testid="banner-upload"
                    />
                    {uploadingBanner && (
                      <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Subiendo banner...
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Logo - Upload Cloudinary */}
              <div className="space-y-2">
                <Label>
                  Logo
                  <span className="text-xs text-muted-foreground ml-2">
                    (Recomendado: 500x500, fondo transparente opcional, máx 2MB)
                  </span>
                </Label>
                <div className="flex items-start gap-4">
                  {formData.logo ? (
                    <div className="relative w-20 h-20 rounded-lg overflow-hidden border">
                      <img 
                        src={formData.logo} 
                        alt="Logo" 
                        className="w-full h-full object-contain bg-white"
                      />
                      <button
                        type="button"
                        onClick={removeLogo}
                        className="absolute top-0 right-0 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                      >
                        <X className="h-3 w-3 text-white" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-20 h-20 rounded-lg border-2 border-dashed border-border flex items-center justify-center">
                      <Store className="h-8 w-8 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1">
                    <Input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleLogoUpload}
                      disabled={uploadingLogo}
                      className="max-w-[250px]"
                      data-testid="logo-upload"
                    />
                    {uploadingLogo && (
                      <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Subiendo logo...
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Operación */}
          <Card>
            <CardHeader>
              <CardTitle>Configuración de Operación</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="horario_apertura">Hora Apertura</Label>
                  <Input
                    id="horario_apertura"
                    type="time"
                    value={formData.horario_apertura}
                    onChange={(e) => handleChange('horario_apertura', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="horario_cierre">Hora Cierre</Label>
                  <Input
                    id="horario_cierre"
                    type="time"
                    value={formData.horario_cierre}
                    onChange={(e) => handleChange('horario_cierre', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="tiempo_preparacion_min">Tiempo Prep. (min)</Label>
                  <Input
                    id="tiempo_preparacion_min"
                    type="number"
                    min="5"
                    max="120"
                    value={formData.tiempo_preparacion_min}
                    onChange={(e) => handleChange('tiempo_preparacion_min', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="costo_envio">
                    Costo de Envío ({paisActual?.simbolo || 'Q'})
                  </Label>
                  <Input
                    id="costo_envio"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.costo_envio}
                    onChange={(e) => handleChange('costo_envio', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="comision_porcentaje">Comisión Mercapoli (%)</Label>
                  <Input
                    id="comision_porcentaje"
                    type="number"
                    step="0.5"
                    min="0"
                    max="50"
                    value={formData.comision_porcentaje}
                    onChange={(e) => handleChange('comision_porcentaje', e.target.value)}
                  />
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    id="activo"
                    checked={formData.activo}
                    onCheckedChange={(v) => handleChange('activo', v)}
                  />
                  <Label htmlFor="activo">Comercio Activo</Label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Datos Fiscales y Bancarios */}
          <Card>
            <CardHeader>
              <CardTitle>Datos Fiscales y Bancarios</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="nit_rfc">
                    {formData.pais === 'MX' ? 'RFC' : 'NIT'}
                  </Label>
                  <Input
                    id="nit_rfc"
                    value={formData.nit_rfc}
                    onChange={(e) => handleChange('nit_rfc', e.target.value)}
                    placeholder={formData.pais === 'MX' ? 'XAXX010101000' : '1234567-8'}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="razon_social">Razón Social</Label>
                  <Input
                    id="razon_social"
                    value={formData.razon_social}
                    onChange={(e) => handleChange('razon_social', e.target.value)}
                    placeholder="Nombre legal de la empresa"
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="banco">Banco</Label>
                  <Input
                    id="banco"
                    value={formData.banco}
                    onChange={(e) => handleChange('banco', e.target.value)}
                    placeholder="Ej: Banrural, Banco Industrial"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="cuenta_banco">Cuenta Bancaria</Label>
                  <Input
                    id="cuenta_banco"
                    value={formData.cuenta_banco}
                    onChange={(e) => handleChange('cuenta_banco', e.target.value)}
                    placeholder="Número de cuenta"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Ubicación */}
          <Card>
            <CardHeader>
              <CardTitle>Ubicación (Opcional)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="latitud">Latitud</Label>
                  <Input
                    id="latitud"
                    type="number"
                    step="0.000001"
                    value={formData.latitud || ''}
                    onChange={(e) => handleChange('latitud', e.target.value || null)}
                    placeholder="14.634915"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="longitud">Longitud</Label>
                  <Input
                    id="longitud"
                    type="number"
                    step="0.000001"
                    value={formData.longitud || ''}
                    onChange={(e) => handleChange('longitud', e.target.value || null)}
                    placeholder="-90.506882"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Botón guardar móvil */}
          <div className="sm:hidden">
            <Button 
              type="submit"
              disabled={saving}
              className="w-full bg-brand-primary hover:bg-brand-secondary"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Guardar Comercio
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RestaurantForm;
